import { useState, useMemo } from "react";

const COLORS = [
  "#2563EB",
  "#10B981",
  "#F97316",
  "#EF4444",
  "#8B5CF6",
  "#0EA5E9",
];

const PieChart = ({
  data = [],
  size = 180,
  centerSubLabel = "Tổng số",
  legendPosition = "bottom",
  showLegend = true,
  totalFormatter,
}) => {
  const [hoverIndex, setHoverIndex] = useState(null);

  const chartData = useMemo(() => data.filter((i) => i.value > 0), [data]);

  const totalValue = useMemo(
    () => chartData.reduce((sum, i) => sum + i.value, 0),
    [chartData],
  );

  const displayTotal = totalFormatter
    ? totalFormatter(totalValue)
    : new Intl.NumberFormat("vi-VN").format(totalValue);

  const r = size / 2 - 8;
  const innerR = r * 0.6;
  const cx = size / 2;
  const cy = size / 2;

  const Wrapper =
    legendPosition === "bottom"
      ? "flex flex-col items-center gap-4"
      : "flex items-center gap-8";

  if (!chartData.length) return null;

  if (chartData.length === 1) {
    const color = COLORS[0];

    return (
      <div className={Wrapper}>
        <ChartCore
          size={size}
          r={r}
          innerR={innerR}
          cx={cx}
          cy={cy}
          centerSubLabel={centerSubLabel}
          centerValue={displayTotal}
        >
          <circle cx={cx} cy={cy} r={r} fill={color} />
          <circle cx={cx} cy={cy} r={innerR} fill="#fff" />
        </ChartCore>

        {showLegend && (
          <Legend
            data={chartData}
            hoverIndex={hoverIndex}
            setHoverIndex={setHoverIndex}
            direction={legendPosition}
          />
        )}
      </div>
    );
  }

  let angle = -90;
  const rad = (d) => (Math.PI * d) / 180;

  return (
    <div className={Wrapper}>
      <ChartCore
        size={size}
        r={r}
        innerR={innerR}
        cx={cx}
        cy={cy}
        centerSubLabel={centerSubLabel}
        centerValue={displayTotal}
      >
        {chartData.map((item, idx) => {
          const a = (item.value / totalValue) * 360;
          const start = angle;
          angle += a;

          const x1 = cx + r * Math.cos(rad(start));
          const y1 = cy + r * Math.sin(rad(start));
          const x2 = cx + r * Math.cos(rad(angle));
          const y2 = cy + r * Math.sin(rad(angle));

          const xi1 = cx + innerR * Math.cos(rad(start));
          const yi1 = cy + innerR * Math.sin(rad(start));
          const xi2 = cx + innerR * Math.cos(rad(angle));
          const yi2 = cy + innerR * Math.sin(rad(angle));

          const active = hoverIndex === null || hoverIndex === idx;

          return (
            <path
              key={idx}
              d={`
                M ${x1} ${y1}
                A ${r} ${r} 0 ${a > 180 ? 1 : 0} 1 ${x2} ${y2}
                L ${xi2} ${yi2}
                A ${innerR} ${innerR} 0 ${a > 180 ? 1 : 0} 0 ${xi1} ${yi1}
                Z
              `}
              fill={COLORS[idx % COLORS.length]}
              opacity={active ? 1 : 0.3}
              onMouseEnter={() => setHoverIndex(idx)}
              onMouseLeave={() => setHoverIndex(null)}
              className="transition-opacity cursor-pointer"
            />
          );
        })}
      </ChartCore>

      {showLegend && (
        <Legend
          data={chartData}
          hoverIndex={hoverIndex}
          setHoverIndex={setHoverIndex}
          direction={legendPosition}
        />
      )}
    </div>
  );
};

const ChartCore = ({
  size,
  r,
  innerR,
  cx,
  cy,
  centerSubLabel,
  centerValue,
  children,
}) => (
  <div className="relative flex-shrink-0">
    <svg width={size} height={size}>
      {children}
    </svg>

    <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none">
      <span className="text-xs text-gray-500">{centerSubLabel}</span>
      <span className="text-xl font-semibold text-gray-900">{centerValue}</span>
    </div>
  </div>
);

/* ================= LEGEND ================= */

const Legend = ({ data, hoverIndex, setHoverIndex, direction }) => {
  const isBottom = direction === "bottom";

  return (
    <div
      className={
        isBottom
          ? "grid grid-col gap-x-6 gap-y-2 w-full max-w-md"
          : "flex flex-col gap-3 min-w-[220px]"
      }
    >
      {data.map((item, idx) => {
        const active = hoverIndex === null || hoverIndex === idx;

        return (
          <div
            key={idx}
            onMouseEnter={() => setHoverIndex(idx)}
            onMouseLeave={() => setHoverIndex(null)}
            className={`flex items-center justify-between gap-3 cursor-pointer transition-opacity ${
              active ? "opacity-100" : "opacity-40"
            }`}
          >
            <div className="flex items-center gap-2 min-w-0">
              <span
                className="w-3 h-3 rounded-full"
                style={{ backgroundColor: COLORS[idx % COLORS.length] }}
              />
              <span className="text-sm text-gray-700 truncate">
                {item.label}
                <span className="text-gray-500 ml-1">({item.percent}%)</span>
              </span>
            </div>

            <span className="text-sm font-medium text-gray-900 tabular-nums">
              {new Intl.NumberFormat("vi-VN").format(item.value)}
            </span>
          </div>
        );
      })}
    </div>
  );
};

export default PieChart;
